import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  APP_ID,
  ApplicationConfig,
  Provider,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import {
  TranslateLoader,
  MissingTranslationHandler,
  provideTranslateService,
  InterpolatableTranslationObject,
} from '@ngx-translate/core';
import { appRoutes } from './app.routes';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { CommonMissingTranslationHandler } from '@pyg-services/language/translates-missing-handler';
import { LanguageService } from '@pyg-services/language/language.service';
import { provideShareButtonsOptions } from 'ngx-sharebuttons';
import { shareIcons } from 'ngx-sharebuttons/icons';
import { TranslatesBrowserLoaderService } from '@pyg-services/language/translates-browser-loader.service';

const httpLoaderFactory = (): TranslateLoader => {
  return new TranslatesBrowserLoaderService();
};

const appInitializer = (): Promise<InterpolatableTranslationObject> => {
  const lang = inject(LanguageService);
  return firstValueFrom(lang.initLanguage());
};

const missingTranslationHandler: Provider = {
  provide: MissingTranslationHandler,
  useClass: CommonMissingTranslationHandler,
};

const loader: Provider = {
  provide: TranslateLoader,
  useFactory: httpLoaderFactory,
};

const provideAppID = (): Provider => ({
  provide: APP_ID,
  useValue: 'serverApp',
});

const provideRecaptchaSiteKey = (): Provider => ({
  provide: RECAPTCHA_V3_SITE_KEY,
  useValue: environment.recaptcha_site_key,
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withFetch()),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ anchorScrolling: 'enabled' }),
      withViewTransitions(),
      withPreloading(PreloadAllModules),
    ),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({ includePostRequests: true }),
    ),
    provideAppInitializer(appInitializer),
    provideTranslateService({ missingTranslationHandler, loader }),
    provideShareButtonsOptions(shareIcons()),
    importProvidersFrom(
      BrowserModule,
      ReactiveFormsModule,
      BrowserAnimationsModule,
      RecaptchaV3Module,
    ),
    provideAppID(),
    provideRecaptchaSiteKey(),
  ],
};
